import React, { useEffect, useMemo, useCallback, useState } from 'react';
import styles from './LessonEdit.module.scss';
import { SllModule } from '@types';
import Header from '@components/header/Header';
import { useParams } from 'react-router-dom';
import Spinner from '@components/spinner/Spinner';
import { EditLessonProvider, useEditLesson } from '../useEditLesson';
import ArrowTop from 'src/assets/icons/chewron_top.svg?react';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import { notEmpty } from '@utils/helpers';
import EditLessonContent from './edit-lesson/EditLessonContent';
import { useSllGetLessonInfoForSaQuery, LessonType as TeacherLessonType } from '@api/graphql-codegen';
import LessonPreparations from './lessonPreparations/LessonPreparations';
import LessonSectionSidebar from './LessonSectionSidebar';
import EditContent from './edit-content/EditContent';
import { sortByPosition } from '@utils/helpers';
import FooterPage from '@src/components/footer-page/FooterPage';

type Params = { id: string; }

function SuperLessonEdit () {
  const { id } = useParams<Params>();
  const [opened, setOpened] = useState(true);

  const { lesson, setSelectionLevel, selectionLevel, setContent, setLesson } = useEditLesson();

  const { loading, data: lessonData, refetch } = useSllGetLessonInfoForSaQuery({ variables: { input: {id}} });

  useEffect(() => {
    if (lessonData?.sllGetLessonInfoForSA.id) {
      setLesson(lessonData?.sllGetLessonInfoForSA);
    }
  }, [setLesson, lessonData?.sllGetLessonInfoForSA]);

  const handleSelectLesson = useCallback(() => {
    if (lessonData?.sllGetLessonInfoForSA?.id) {
      setSelectionLevel('Lesson');
      setLesson(lessonData?.sllGetLessonInfoForSA);
      setContent(undefined);
    }
  }, [lessonData?.sllGetLessonInfoForSA, setSelectionLevel, setContent, setLesson]);

  const handleClose = useCallback((e : React.SyntheticEvent) => {
    e.stopPropagation();
    setOpened(state => !state);
  }, []);

  const listwrapper = useMemo(() => {
    const classes = [styles.wrapper];

    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(' ');
  }, [opened]);

  const contentClasses = useMemo(() => {
    const classes = [styles.list];

    return classes.join(' ');
  }, []);

  const content = useMemo(() => {
    switch (selectionLevel) {
      case 'Lesson': {
        return <EditLessonContent refetchLesson={refetch}/>;
      }
      case 'Content':
      case 'SectionContent': {
        return <EditContent />;
      }
      default: {
        return <EditLessonContent refetchLesson={refetch}/>;
      }
    }
  }, [selectionLevel, refetch]);

  const lessonMaterials: SllModule[] = useMemo(() => {
    const materials = [...lesson?.materials || []].filter(notEmpty);
    materials.sort(sortByPosition);
    const updatedMaterials = materials.filter(notEmpty);
    return updatedMaterials;
  }, [lesson?.materials]);

  return (
    <>
      <Header />
      {(loading) ? <div className={styles.loading}><Spinner/></div> :
      <div className={styles.page}>
        <div className={styles.mainWrapper}>
          <div className={listwrapper}>
            <div onClick={handleSelectLesson} className={styles.programName}>
              <div className={styles.bolderPN}>
                Edit Lesson<br/><span className={styles.ligtherPN}>{lesson?.name || ''}</span>
              </div>
            </div>
            <section onClick={handleClose} className={styles.header}>
              <div className={styles.headerContent}>
                <div className={styles.title}>{lesson?.name || ''}</div>
              </div>
              <div className={styles.iconWrapper} onClick={handleClose}>
                <ArrowTop className={styles.icon}/>
              </div>
              <div className={styles.editIconWrapper} onClick={handleSelectLesson}>
                <IconPencil/>
              </div>
            </section>
            <section className={contentClasses}>
              <LessonPreparations
                lessonOpened={false}
                lessonType={TeacherLessonType.PREPARATION}
                contents={lessonMaterials}
                title="Lesson Materials"
              />
              {lesson ? (
                <LessonSectionSidebar
                  lesson={lesson}
                  refetchLesson={refetch}
                />
              ) : null}
            </section>
          </div>
          <div className={styles.content}>
            {content}
          </div>
        </div>
        <FooterPage />
      </div>
      }
    </>
  );
}

const LessonEdit = () => {
  return (
    <EditLessonProvider>
      <SuperLessonEdit />
    </EditLessonProvider>
  )
};

export default LessonEdit;
